/**
 * SidebarRecentTasks — the member sidebar's Super-Agent task peek.
 *
 * Members have no history section: their per-agent chats surface on the agent
 * rows (see SidebarAgentChats), and each agent keeps a History tab. Super-Agent
 * chats belong to no agent row, so before this they were reachable only through
 * the search palette — a member could be mid-task and find nothing in the rail.
 *
 * Deliberately NOT the admin `SidebarAllChats`: this is a short bounded peek
 * (newest {@link MAX_TASKS}) that stays pinned while the agent list below it
 * scrolls, so a long task history can never crowd out the agents. Overflow goes
 * to "See all", which opens the search palette that already renders the full
 * history grouped by date.
 *
 * Rows are NOT a lookalike of the admin history — they share its actual
 * implementation (`chat-row-live-state`), so the per-chat AI icon, the
 * streaming spinner, the typed-title animation and the delete affordance are
 * the same code, not a second copy that drifts.
 */

import { useCallback, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Loader2, Trash2 } from 'lucide-react'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from '@/components/ui/sidebar'
import { DeleteChatDialog } from '@/components/chat/delete-chat-dialog'
import { useChatHistory } from '@/hooks/use-chat-history'
import { SUPER_AGENT_ID, isSuperAgentId } from '@taqat/core/super-agent'
import type { ChatHistoryApiItem } from '@taqat/core'
import { ChatRowIcon, ChatRowTitle, useChatRowLiveState } from './chat-row-live-state'

/** How many tasks the peek shows before deferring to "See all". */
const MAX_TASKS = 4

/** Most recent activity on a chat: `updatedAt` when present, else `createdAt`. */
function chatTimestamp(chat: ChatHistoryApiItem): number {
  const raw = chat.updatedAt || chat.createdAt
  const ms = raw ? new Date(raw).getTime() : 0
  return Number.isNaN(ms) ? 0 : ms
}

/**
 * Compact age label: `2h`, `3d`, `5w`. Empty when the timestamp is unusable, so
 * a missing date renders nothing rather than "NaN". Replaces the admin list's
 * date-group headers: at four rows those cost more height than they explain.
 */
function relativeAge(ms: number): string {
  if (!ms) return ''
  const diff = Date.now() - ms
  if (diff < 0) return 'now'
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return 'now'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  const weeks = Math.floor(days / 7)
  if (weeks < 52) return `${weeks}w`
  return `${Math.floor(days / 365)}y`
}

export function SidebarRecentTasks({
  appId,
  onSeeAll,
  excludeChatIds = [],
}: {
  appId?: string
  /** Opens the search palette — the home for the full history. */
  onSeeAll: () => void
  /** Chat ids to hide (desktop: CoWork tasks live in their own list). */
  excludeChatIds?: string[]
}) {
  const location = useLocation()
  const navigate = useNavigate()
  // Same endpoint the agent rows read, so this costs no extra request.
  const { state, refetch } = useChatHistory({ limit: 50, autoFetch: true })

  const handleDeleted = useCallback(
    (deletedChatId: string) => {
      // Leave a chat that no longer exists before its page 404s. Read
      // window.location directly: a fresh chat rewrites the URL via
      // history.replaceState, which React Router's snapshot can lag behind.
      const livePath = window.location.pathname
      if (livePath.includes(`/chat/${deletedChatId}`)) {
        navigate(`/app/${appId ?? ''}/chat`)
      }
      void refetch()
    },
    [appId, navigate, refetch],
  )

  const {
    animatingTitles,
    streamingChatIds,
    deletingChatIds,
    iconOverrides,
    handleDeletingChange,
  } = useChatRowLiveState({ onDeleted: handleDeleted })

  const { tasks, hasMore } = useMemo(() => {
    const exclude = new Set(excludeChatIds)
    const superAgentChats = state.conversations
      // A chat with no agentId is a super-agent chat: older rows predate the
      // sentinel, so matching on `isSuperAgentId` alone would strand them.
      .filter(
        (chat) =>
          !exclude.has(chat.id) && (!chat.agentId || isSuperAgentId(chat.agentId)),
      )
      .sort((a, b) => chatTimestamp(b) - chatTimestamp(a))

    return {
      tasks: superAgentChats.slice(0, MAX_TASKS),
      hasMore: superAgentChats.length > MAX_TASKS,
    }
  }, [state.conversations, excludeChatIds])

  // First load, before anything is known: skeletons rather than a pop-in, the
  // same treatment the admin history gives its own initial fetch.
  if (state.loading && state.conversations.length === 0) {
    return (
      <SidebarGroup className="shrink-0 py-1">
        <SidebarGroupLabel className="px-3 text-muted-foreground group-data-[collapsible=icon]:hidden">
          Recent tasks
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {Array.from({ length: 3 }).map((_, index) => (
              <SidebarMenuItem key={`recent-task-skeleton-${index}`}>
                <SidebarMenuSkeleton showIcon />
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    )
  }

  // No tasks yet → no section at all. An empty state here would be a label
  // pointing at nothing, directly above the list the member actually wants.
  if (tasks.length === 0) return null

  const basePath = `/app/${appId || ''}`

  return (
    /* `shrink-0` keeps the peek pinned: the agent list below owns the scroll,
       so tasks stay put while a member scrolls their agents. */
    <SidebarGroup className="shrink-0 py-1">
      <SidebarGroupLabel className="flex w-full items-center justify-between px-3 text-muted-foreground group-data-[collapsible=icon]:hidden">
        <span>Recent tasks</span>
        {hasMore && (
          <button
            type="button"
            onClick={onSeeAll}
            data-testid="sidebar-recent-tasks-see-all"
            className="rounded text-xs font-medium text-muted-foreground/80 transition-colors hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          >
            See all
          </button>
        )}
      </SidebarGroupLabel>

      <SidebarGroupContent>
        <SidebarMenu data-testid="sidebar-recent-tasks">
          {tasks.map((task) => {
            const age = relativeAge(chatTimestamp(task))
            return (
              <SidebarMenuItem key={task.id}>
                <SidebarMenuButton
                  isActive={location.pathname.includes(`/chat/${task.id}`)}
                  asChild
                >
                  <Link
                    to={`${basePath}/agents/${SUPER_AGENT_ID}/chat/${task.id}`}
                    data-testid="sidebar-recent-task-row"
                    className="flex min-w-0 items-center gap-3 pr-8"
                  >
                    <ChatRowIcon
                      isStreaming={streamingChatIds.has(task.id)}
                      iconName={iconOverrides[task.id] ?? task.metadata?.icon ?? null}
                    />
                    <span className="truncate">
                      <ChatRowTitle
                        chatId={task.id}
                        title={task.title}
                        animatingTitles={animatingTitles}
                      />
                    </span>
                    {/* Hidden on hover so it never collides with the delete
                        action that occupies the same corner. */}
                    {age && (
                      <span className="ml-auto shrink-0 text-[10px] tabular-nums text-muted-foreground group-hover/menu-item:opacity-0">
                        {age}
                      </span>
                    )}
                  </Link>
                </SidebarMenuButton>
                <DeleteChatDialog
                  chatId={task.id}
                  onDeleted={() => {}}
                  onDeletingChange={(isDeleting) => handleDeletingChange(task.id, isDeleting)}
                >
                  <SidebarMenuAction
                    showOnHover={!deletingChatIds.has(task.id)}
                    aria-label="Delete chat"
                    disabled={deletingChatIds.has(task.id)}
                    className="text-muted-foreground hover:text-destructive data-[disabled]:opacity-100"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {deletingChatIds.has(task.id)
                      ? <Loader2 className="animate-spin text-destructive" />
                      : <Trash2 />}
                  </SidebarMenuAction>
                </DeleteChatDialog>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
